import React from "react";
import ArticleLayout, { Step, Tip, CodeBlock, Check, Ext } from "./ArticleLayout";
import ProtectedEmail from "@/components/ProtectedEmail";

// ============================================================
//  WEBSITE WIDGET
// ============================================================
export function WebsiteWidgetGuide() {
  const origin = typeof window !== "undefined" ? window.location.origin : "https://agentreply.io";
  return (
    <ArticleLayout
      kicker="How-to guide"
      title="Embed the chat widget on your website."
      intro="One line of code adds a floating chat bubble to any website. Works on Shopify, WordPress, Webflow, Wix, plain HTML — anywhere you can paste a script tag."
    >
      <Step n="1" title="Grab your snippet">
        <p>Open your agent → <strong className="text-[#F5F2EB]">Share / Embed</strong> tab. Copy the snippet — it looks like:</p>
        <CodeBlock>{`<script src="${origin}/widget.js" data-token="YOUR_AGENT_TOKEN"></script>`}</CodeBlock>
        <Tip>The token is unique per agent. Don't share it publicly until you're happy with how your agent behaves — once it's out there, anyone with the link can chat.</Tip>
      </Step>

      <Step n="2" title="Customise the look">
        <p>On the same tab, scroll to <strong className="text-[#F5F2EB]">Customise your widget</strong>. Update:</p>
        <ul className="list-none space-y-1 pl-1">
          <Check>Brand colour (matches your launcher + send button)</Check>
          <Check>Position (bottom-right or bottom-left)</Check>
          <Check>Header text, welcome message, launcher label</Check>
          <Check>Upload a logo (PNG/JPG/SVG, max 250KB) — appears in launcher + chat header</Check>
        </ul>
        <p>Click <strong className="text-[#F5F2EB]">Save theme</strong>. Changes apply instantly to every site running your snippet.</p>
      </Step>

      <Step n="3" title="Paste it into your site">
        <p><strong className="text-[#F5F2EB]">Shopify:</strong> Online Store → Themes → Edit code → <code>theme.liquid</code> → paste the snippet just before <code>&lt;/body&gt;</code>. Save.</p>
        <p><strong className="text-[#F5F2EB]">WordPress:</strong> Appearance → Theme File Editor → <code>footer.php</code> → paste before <code>&lt;/body&gt;</code>. Or use the free <em>Insert Headers and Footers</em> plugin.</p>
        <p><strong className="text-[#F5F2EB]">Webflow:</strong> Project Settings → Custom Code → <em>Footer Code</em> → paste → Save → Publish.</p>
        <p><strong className="text-[#F5F2EB]">Wix:</strong> Settings → Custom Code → Add Custom Code → paste → "Body — end" → Apply to all pages.</p>
        <p><strong className="text-[#F5F2EB]">Plain HTML:</strong> Paste before <code>&lt;/body&gt;</code> in every page (or your site-wide template).</p>
      </Step>

      <Step n="4" title="Test it">
        <p>Open your site in a private/incognito window. You'll see the chat bubble in the corner. Click it, send a message, your agent replies.</p>
        <Tip>If you don't see the bubble, hard-refresh (Cmd+Shift+R / Ctrl+Shift+F5) and check the browser console (F12) for errors. The most common issue is a stripped <code>&lt;/body&gt;</code> tag in a heavily-cached CDN.</Tip>
      </Step>

      <Step n="5" title="What happens to visitor data">
        <p>Each visitor gets a unique anonymous ID stored in their browser's localStorage. You'll see them appear in the <strong className="text-[#F5F2EB]">Contacts</strong> tab with their name (if they shared it), conversation history, and any details the AI captured via intake forms.</p>
        <p>For privacy compliance, see our <a href="/legal/privacy" className="text-[#D95B38] hover:underline">Privacy Policy</a> and link it from your own site's footer.</p>
      </Step>
    </ArticleLayout>
  );
}

// ============================================================
//  SHARE LINK
// ============================================================
export function ShareLinkGuide() {
  return (
    <ArticleLayout
      kicker="How-to guide"
      title="Share your agent with a public link."
      intro="Every agent comes with a standalone chat page (e.g. agentreply.io/chat/abc123). Perfect for QR codes, link-in-bio, and email signatures — no website needed."
    >
      <Step n="1" title="Find your link">
        <p>Open your agent → <strong className="text-[#F5F2EB]">Share / Embed</strong> tab → copy the public URL.</p>
      </Step>

      <Step n="2" title="Make a QR code (free, 1 minute)">
        <p>Paste your URL into <Ext href="https://www.qrcode-monkey.com/">qrcode-monkey.com</Ext> or <Ext href="https://qr.io">qr.io</Ext>. Both let you:</p>
        <ul className="list-none space-y-1 pl-1">
          <Check>Add your logo to the centre of the QR code</Check>
          <Check>Pick a brand colour</Check>
          <Check>Download high-resolution PNG/SVG for print</Check>
        </ul>
        <Tip>Stick QR codes on shop windows, business cards, restaurant tables, event posters — anywhere your customers wait.</Tip>
      </Step>

      <Step n="3" title="Add to Instagram / TikTok bio">
        <p>Instagram and TikTok only allow one link in your bio. Two options:</p>
        <ul className="list-none space-y-1 pl-1">
          <Check>Use your agent's URL directly</Check>
          <Check>Or add it to a link-in-bio tool: <Ext href="https://linktr.ee">Linktree</Ext>, <Ext href="https://beacons.ai">Beacons</Ext>, <Ext href="https://bento.me">Bento</Ext></Check>
        </ul>
      </Step>

      <Step n="4" title="Email signature">
        <p>Paste this into your Gmail/Outlook signature (replace the URL):</p>
        <CodeBlock>{`💬 Chat with me anytime: https://agentreply.io/chat/YOUR_TOKEN`}</CodeBlock>
      </Step>

      <Step n="5" title="Google Business profile">
        <p>If you have a Google Business listing, add your chat URL as a "Website" or "Appointment link". Customers searching for you on Google can start chatting in one click.</p>
      </Step>
    </ArticleLayout>
  );
}

// ============================================================
//  KNOWLEDGE BASE
// ============================================================
export function KnowledgeBaseGuide() {
  return (
    <ArticleLayout
      kicker="How-to guide"
      title="Train your agent with documents."
      intro="Upload PDFs, CSVs, menus, FAQs — your agent uses them to answer customer questions accurately, in your exact tone, without making things up."
    >
      <Step n="1" title="What you can upload">
        <p>Currently supported:</p>
        <ul className="list-none space-y-1 pl-1">
          <Check><strong className="text-[#F5F2EB]">PDF</strong> — menus, brochures, policies, manuals</Check>
          <Check><strong className="text-[#F5F2EB]">CSV</strong> — product catalogues, prices, opening hours by day</Check>
          <Check><strong className="text-[#F5F2EB]">TXT / Markdown</strong> — FAQs, scripts, plain notes</Check>
        </ul>
        <p>Max ~5 MB per file. Most files use 1-3 MB.</p>
      </Step>

      <Step n="2" title="Upload">
        <p>Agent → <strong className="text-[#F5F2EB]">Knowledge</strong> tab → drag files in or click <strong className="text-[#F5F2EB]">Upload</strong>. Processing takes a few seconds per file.</p>
        <Tip>The first time you upload, your agent answers using the new content within ~5 seconds. No re-training needed.</Tip>
      </Step>

      <Step n="3" title="Format tips so the AI finds the right answers">
        <ul className="list-none space-y-1 pl-1">
          <Check><strong className="text-[#F5F2EB]">Use clear headings.</strong> "Opening hours" beats "Time we open". The AI matches keywords.</Check>
          <Check><strong className="text-[#F5F2EB]">Repeat the question in your answer.</strong> "What's your return policy? Our return policy is..." performs better than a bare paragraph.</Check>
          <Check><strong className="text-[#F5F2EB]">Break long documents into focused files.</strong> One file per topic ("Menu.pdf", "Hours.pdf") beats one giant "Everything.pdf".</Check>
          <Check><strong className="text-[#F5F2EB]">Avoid scanned/image PDFs.</strong> If text isn't selectable, the AI can't read it. Use a real PDF or run OCR first.</Check>
          <Check><strong className="text-[#F5F2EB]">For CSVs:</strong> first row should be column headers (e.g. <code>Product, Price, Description</code>).</Check>
        </ul>
      </Step>

      <Step n="4" title="The 'Quick knowledge' field">
        <p>On the Configure tab there's a free-text "Quick knowledge" field. Use it for things that don't deserve a whole document — e.g. "We're closed Sundays. Free shipping over £50. Owner's name is Sam."</p>
      </Step>

      <Step n="5" title="Test what your agent learned">
        <p>Go to the <strong className="text-[#F5F2EB]">Test chat</strong> tab and ask questions you expect customers to ask. If the answer's wrong, refine the document and re-upload.</p>
      </Step>

      <Step n="6" title="Remove or update a document">
        <p>Knowledge tab → click the trash icon next to any file. Re-upload an updated version to replace it.</p>
      </Step>
    </ArticleLayout>
  );
}

// ============================================================
//  INTAKE FORM
// ============================================================
export function IntakeFormGuide() {
  return (
    <ArticleLayout
      kicker="How-to guide"
      title="Capture leads with an intake form."
      intro="Have your agent ask for a name, phone, email — whatever you need — naturally during conversation. Captured values land on the contact's profile in your CRM."
    >
      <Step n="1" title="Add a field">
        <p>Agent → <strong className="text-[#F5F2EB]">Configure</strong> tab → scroll to <strong className="text-[#F5F2EB]">Intake form</strong> → click <strong className="text-[#F5F2EB]">+ Add field</strong>.</p>
        <p>Each field has three properties:</p>
        <ul className="list-none space-y-1 pl-1">
          <Check><strong className="text-[#F5F2EB]">Key</strong> — internal name (e.g. <code>phone</code>, <code>budget</code>, <code>service_needed</code>)</Check>
          <Check><strong className="text-[#F5F2EB]">Question</strong> — exact wording your agent uses (e.g. "What's the best number to reach you on?")</Check>
          <Check><strong className="text-[#F5F2EB]">Confirm</strong> — when ticked, the AI reads the value back to confirm ("Just to confirm — that's 0712 345 6789, correct?")</Check>
        </ul>
      </Step>

      <Step n="2" title="Use the phone preset">
        <p>Click <strong className="text-[#F5F2EB]">+ Phone (with read-back)</strong> for a one-click preset that asks for phone and confirms the digits — critical for accuracy on voice notes.</p>
      </Step>

      <Step n="3" title="How the AI uses your form">
        <p>The AI weaves your questions naturally into conversation — it won't interrogate the customer. Example flow:</p>
        <CodeBlock>{`Customer: "Hi do you do haircuts on Sundays?"
Agent:    "We're closed Sundays — Monday to Saturday only.
           What's your name, by the way?"
Customer: "I'm Alex"
Agent:    "Nice to meet you Alex! What's the best
           number to reach you on?"`}</CodeBlock>
        <Tip>Order matters. List the most important field first — if the customer drops off, you still got the high-value answer.</Tip>
      </Step>

      <Step n="4" title="Where the answers go">
        <p>Captured values appear on the contact's profile in the <strong className="text-[#F5F2EB]">Contacts</strong> tab. Phone, email, and name auto-promote to top-level fields so you can click-to-call or click-to-email.</p>
      </Step>

      <Step n="5" title="Export your leads">
        <p>Currently: copy from the Contacts tab. (CSV export coming soon — <ProtectedEmail display="email us" /> if you need it urgently.)</p>
      </Step>
    </ArticleLayout>
  );
}

// ============================================================
//  PAUSE AI
// ============================================================
export function PauseAIGuide() {
  return (
    <ArticleLayout
      kicker="How-to guide"
      title="Take over conversations manually."
      intro="Some customers deserve a human. Here's how to pause the AI on individual conversations, whitelist VIP contacts, and make sure nothing falls through the cracks."
    >
      <Step n="1" title="Pause AI on a single conversation">
        <p>Agent → <strong className="text-[#F5F2EB]">Contacts</strong> tab → click any contact → toggle <strong className="text-[#F5F2EB]">Pause AI</strong>.</p>
        <p>From that point on, any message they send gets your configured fallback reply (default: "Thanks for your message — someone will get back to you shortly.") and is flagged as <em>followup</em> for you to handle.</p>
      </Step>

      <Step n="2" title="Customise the fallback message">
        <p>Agent → <strong className="text-[#F5F2EB]">Configure</strong> tab → <strong className="text-[#F5F2EB]">Message sent when AI auto-reply is paused</strong>. Make it warm and specific:</p>
        <CodeBlock>{`Hi! Sam here — I got your message and will reply
personally within an hour. Thanks for your patience!`}</CodeBlock>
      </Step>

      <Step n="3" title="Whitelist VIP numbers (auto-pause)">
        <p>Agent → <strong className="text-[#F5F2EB]">Configure</strong> → <strong className="text-[#F5F2EB]">Manual contacts</strong>. Paste phone numbers or contact IDs (one per line). The first time any of them message, the AI is paused for them automatically.</p>
        <Tip>Perfect for: existing customers you already talk to personally, suppliers, family. Or to never let the AI talk to journalists or competitors.</Tip>
      </Step>

      <Step n="4" title="Find conversations needing follow-up">
        <p>Contacts tab → filter <strong className="text-[#F5F2EB]">Followup</strong>. Shows everyone who messaged while AI was paused.</p>
        <p>Each contact card has quick-action buttons: <strong className="text-[#F5F2EB]">📧 Email</strong>, <strong className="text-[#F5F2EB]">📞 Call</strong>, <strong className="text-[#F5F2EB]">💬 WhatsApp</strong> — one click to reach them.</p>
      </Step>

      <Step n="5" title="Resume the AI">
        <p>Once you've handled the conversation, toggle <strong className="text-[#F5F2EB]">Pause AI</strong> off and mark the contact as <strong className="text-[#F5F2EB]">Resolved</strong>. The AI takes over again on their next message.</p>
      </Step>
    </ArticleLayout>
  );
}

// ============================================================
//  CUSTOM DOMAIN
// ============================================================
export function CustomDomainGuide() {
  return (
    <ArticleLayout
      kicker="How-to guide"
      title="Connect your custom domain."
      intro="Run AgentReply on your own domain so customers chat with you on yourbrand.com — not agentreply.io."
    >
      <div className="bg-[#1A1715] border-l-2 border-[#D95B38] px-5 py-4 mb-12 text-[#A39A8F] font-mono text-xs">
        <span className="text-[#D95B38] uppercase tracking-[0.25em] mr-2">Note</span>
        Custom domains become available after we deploy your app to production. If you're still testing locally, skip this for now — your preview URL works fine.
      </div>

      <Step n="1" title="Buy or use an existing domain">
        <p>Most popular registrars: <Ext href="https://godaddy.com">GoDaddy</Ext>, <Ext href="https://namecheap.com">Namecheap</Ext>, <Ext href="https://porkbun.com">Porkbun</Ext>, <Ext href="https://cloudflare.com">Cloudflare</Ext>. A <code>.com</code> or <code>.io</code> costs $10-50/year.</p>
      </Step>

      <Step n="2" title="Decide: root or subdomain?">
        <ul className="list-none space-y-1 pl-1">
          <Check><strong className="text-[#F5F2EB]">Root</strong> (e.g. <code>yourbrand.com</code>) — clean, but you lose your existing site if you have one</Check>
          <Check><strong className="text-[#F5F2EB]">Subdomain</strong> (e.g. <code>chat.yourbrand.com</code>) — best of both worlds, keep your main site untouched</Check>
        </ul>
      </Step>

      <Step n="3" title="Add a CNAME record at your registrar">
        <p>In your registrar's DNS panel, add a new <strong className="text-[#F5F2EB]">CNAME</strong> record:</p>
        <CodeBlock>{`Type:  CNAME
Name:  chat       (or @ for root)
Value: <your-deployment-url>.emergent.host
TTL:   300`}</CodeBlock>
        <Tip>Email <ProtectedEmail display="us" /> with your chosen domain — we'll give you the exact value to paste in.</Tip>
      </Step>

      <Step n="4" title="Wait for DNS propagation">
        <p>15 minutes — 4 hours typically. Check progress at <Ext href="https://dnschecker.org">dnschecker.org</Ext>.</p>
      </Step>

      <Step n="5" title="HTTPS certificate (automatic)">
        <p>Once DNS is live, we provision an SSL certificate automatically. Your domain serves on HTTPS within ~2 minutes of being detected.</p>
      </Step>
    </ArticleLayout>
  );
}

// ============================================================
//  BILLING
// ============================================================
export function BillingGuide() {
  return (
    <ArticleLayout
      kicker="How-to guide"
      title="Billing, plans & invoices."
      intro="Everything you need to know about your AgentReply subscription."
    >
      <Step n="1" title="Your trial">
        <p>Every new account gets a <strong className="text-[#F5F2EB]">14-day free trial</strong> with full Pro access — no credit card required. Build, test, share, embed. When the trial ends, your agents pause until you activate a plan.</p>
      </Step>

      <Step n="2" title="Plans and prices">
        <ul className="list-none space-y-1 pl-1">
          <Check><strong className="text-[#F5F2EB]">Pro Monthly</strong> — $9.99/month, cancel anytime</Check>
          <Check><strong className="text-[#F5F2EB]">Pro Yearly</strong> — $70/year (save ~42%)</Check>
          <Check><strong className="text-[#F5F2EB]">Lifetime</strong> — $250 once, yours forever, no renewals</Check>
        </ul>
        <p>All plans include unlimited agents, all 9 voices, 30+ languages, knowledge base, WhatsApp, embed widget, intake forms, contact CRM, and usage stats.</p>
      </Step>

      <Step n="3" title="Upgrade">
        <p>Dashboard → click <strong className="text-[#F5F2EB]">Activate plan</strong> (or "Upgrade") → pick monthly/yearly/lifetime → Stripe checkout → paid. Your agents reactivate within a few seconds.</p>
      </Step>

      <Step n="4" title="Change plan">
        <p>To switch monthly → yearly (or vice versa), email <ProtectedEmail display="support" subject="Change plan" />. We'll prorate the difference. Self-service plan changes coming soon.</p>
      </Step>

      <Step n="5" title="Cancel">
        <p>Email <ProtectedEmail display="support" subject="Cancel subscription" /> with "Cancel subscription" in the subject. We'll cancel within 24 hours — you keep access until the end of your billing period.</p>
      </Step>

      <Step n="6" title="Invoices & receipts">
        <p>Stripe sends an automatic receipt to your email after each payment. Need a proper invoice for your accountant? Email us with your business name & address and we'll send one.</p>
      </Step>

      <Step n="7" title="Refund policy">
        <p>14-day full refund on monthly/yearly plans — no questions asked. Lifetime is non-refundable after 30 days. See our <a href="/legal/terms" className="text-[#D95B38] hover:underline">Terms</a> for the legal version.</p>
      </Step>
    </ArticleLayout>
  );
}
